import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext();

/* =========================
   ROLE → PERMISSIONS MAP
========================= */

/*
 * PERMISSION → SIDEBAR / ROUTE MAPPING
 * ─────────────────────────────────────────────────────────────────
 * Sidebar gate              Route guard                 Feature
 * ─────────────────────────────────────────────────────────────────
 * overview:view             (none)                      Dashboard
 * core:framework            core:framework              Corporate Structure
 * functional:framework:own  functional:framework:own    Functional Structure
 * strategy:corporate        corporate:tdp               Corporate Strategy
 * strategy:functional       functional:tdp:own          Functional Strategy
 * strategy:role_models      role:model:all:view         Role Talent Strategy
 * planning:role_maps        role:model:self             Role Competency Mapping
 * review:evaluate           core:talent-review          Evaluate
 * reports:corporate         corporate:tdp               Corporate Reports
 * reports:functional        functional:tdp:own          Functional Reports
 * reports:individual        individual:talent-map:self  Individual Reports
 * resources:competency_bank resources:competency_bank   Competency Bank
 * plan:corporate            plan:corporate              Corporate Talent Plan
 * plan:functional           plan:functional             Functional Talent Plan
 * plan:individual           plan:individual             Individual Talent Plan
 * references:view           (none)                      References
 * users:list                users:list                  User Management
 * (no gate)                 (none)                      Profile Settings
 * ─────────────────────────────────────────────────────────────────
 * PLATFORM ADMIN (platform_admin role only)
 * ─────────────────────────────────────────────────────────────────
 * admin:tenants             admin:tenants               Tenant Management
 * admin:billing             admin:billing               Subscription and Billing
 * admin:usage               admin:usage                 Usage Analytics
 * admin:support             admin:support               Support Actions
 * admin:org_health          admin:org_health            Org Health
 * admin:audit               admin:audit                 Audit Trails
 * ─────────────────────────────────────────────────────────────────
 * Every role that shows a sidebar link MUST also carry the matching
 * route permission, otherwise the link is visible but bounces to /dashboard.
 */
const ROLE_PERMISSIONS = {

  // ── Super Admin ─────────────────────────────────────────────────
  // Full platform management: user management + all admin modules.
  super_admin: [
    'users:list',
    'admin:tenants',
    'admin:billing',
    'admin:usage',
    'admin:support',
    'admin:org_health',
    'admin:audit',
  ],

  // ── CEO ─────────────────────────────────────────────────────────
  // Org structure, all strategy, corporate + individual reports,
  // evaluate, resources, references. No role competencies config,
  // no functional performance, no user management.
  ceo: [
    'overview:view',

    'core:framework',
    'functional:framework:own',

    'strategy:corporate',    'corporate:tdp',
    'strategy:functional',   'functional:tdp:own',
    'strategy:role_models',  'role:model:all:view',

    'review:evaluate',       'core:talent-review',

    'reports:corporate',
    'reports:individual',    'individual:talent-map:self',

    'resources:competency_bank',

    'plan:corporate',
    'plan:individual',

    'references:view',
  ],

  // ── CHRO ────────────────────────────────────────────────────────
  // Full operational scope including role competencies config,
  // all three report types, and user management.
  chro: [
    'overview:view',

    'core:framework',
    'functional:framework:own',

    'strategy:corporate',    'corporate:tdp',
    'strategy:functional',   'functional:tdp:own',
    'strategy:role_models',  'role:model:all:view',

    'planning:role_maps',    'role:model:self',

    'review:evaluate',       'core:talent-review',

    'reports:corporate',
    'reports:functional',
    'reports:individual',    'individual:talent-map:self',

    'resources:competency_bank',

    'plan:corporate',
    'plan:functional',
    'plan:individual',

    'references:view',

    'users:list',
  ],

  // ── Function Head ────────────────────────────────────────────────
  // Same operational scope as CHRO but without user management.
  function_head: [
    'overview:view',

    'core:framework',
    'functional:framework:own',

    'strategy:corporate',    'corporate:tdp',
    'strategy:functional',   'functional:tdp:own',
    'strategy:role_models',  'role:model:all:view',

    'planning:role_maps',    'role:model:self',

    'review:evaluate',       'core:talent-review',

    'reports:corporate',
    'reports:functional',
    'reports:individual',    'individual:talent-map:self',

    'resources:competency_bank',

    'plan:corporate',
    'plan:functional',
    'plan:individual',

    'references:view',
  ],

  // ── Individual (default role for new sign-ups) ───────────────────
  // Can view strategy, evaluate, see own performance report
  // and references. No structure, no planning, no resources.
  individual: [
    'overview:view',

    'strategy:corporate',    'corporate:tdp',
    'strategy:functional',   'functional:tdp:own',

    'review:evaluate',       'core:talent-review',

    'reports:individual',    'individual:talent-map:self',

    'plan:individual',

    'references:view',
  ],

  // Alias kept for backwards-compatibility
  user: [
    'overview:view',

    'strategy:corporate',    'corporate:tdp',
    'strategy:functional',   'functional:tdp:own',

    'review:evaluate',       'core:talent-review',

    'reports:individual',    'individual:talent-map:self',

    'plan:individual',

    'references:view',
  ],

  // ── System Admin ────────────────────────────────────────────────
  // Restricted administrative role: user management only.
  system_admin: [
    'users:list',
  ],
};

/* =========================
   PROVIDER
========================= */

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionExpiring, setSessionExpiring] = useState(false);
  const idleWarnRef = useRef(null);
  const idleLogoutRef = useRef(null);

  /**
   * Fetches the user's profile row from Supabase and derives their
   * permission set from ROLE_PERMISSIONS. Uses a direct REST fetch
   * instead of the Supabase client to avoid intermittent hangs.
   * Falls back to 'individual' permissions on any failure.
   */
  const hydrateProfile = async (authUser, tokenOverride = null) => {
    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

      // Use the caller-supplied token first (e.g. from a TOKEN_REFRESHED event),
      // then fall back to sessionStorage, then the anon key.
      let accessToken = tokenOverride || supabaseKey;

      if (!tokenOverride) {
        const storedSession = sessionStorage.getItem('supabase-auth-token');
        if (storedSession) {
          try {
            const sessionData = JSON.parse(storedSession);
            accessToken = sessionData.access_token || supabaseKey;
          } catch (e) {
            // Malformed session — stay with anon key
          }
        }
      }

      const response = await fetch(
        `${supabaseUrl}/rest/v1/profiles?id=eq.${authUser.id}&select=*`,
        {
          headers: {
            'apikey': supabaseKey,
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation'
          }
        }
      );

      if (!response.ok) {
        console.error('Failed to fetch profile:', response.status);
        setUser(authUser);
        setProfile(null);
        setPermissions(ROLE_PERMISSIONS['individual']);
        return;
      }

      const profiles = await response.json();
      const profile = profiles[0];

      if (!profile) {
        console.warn('No profile found for user, using default permissions');
        setUser(authUser);
        setProfile(null);
        setPermissions(ROLE_PERMISSIONS['individual']);
        return;
      }

      const roleKey = profile?.role?.toLowerCase() || 'individual';
      const assignedPermissions = ROLE_PERMISSIONS[roleKey] || ROLE_PERMISSIONS['individual'];

      setUser(authUser);
      setProfile(profile);
      setPermissions(assignedPermissions);
    } catch (error) {
      console.error('Error hydrating profile:', error);
      setUser(authUser);
      setProfile(null);
      setPermissions(ROLE_PERMISSIONS['individual']);
    }
  };

  useEffect(() => {
    let mounted = true;

    /**
     * Initialises auth on mount.
     * 1. Checks sessionStorage for a manually saved session (used to bypass
     *    intermittent Supabase client hangs on first load).
     * 2. Falls back to supabase.auth.getUser() with a 3 s timeout.
     */
    const init = async () => {
      try {
        const storedSession = sessionStorage.getItem('supabase-auth-token');

        if (storedSession) {
          try {
            const sessionData = JSON.parse(storedSession);
            const now = Math.floor(Date.now() / 1000);

            if (sessionData.expires_at && sessionData.expires_at > now) {
              if (mounted && sessionData.user) {
                // Sync the stored tokens to the Supabase client so component-level
                // supabase.from() calls attach the user JWT instead of the anon key.
                // Without this, RLS policies reject writes for new tenants whose
                // session was never set on the client (signup uses raw fetch, not the SDK).
                if (sessionData.access_token && sessionData.refresh_token) {
                  supabase.auth.setSession({
                    access_token: sessionData.access_token,
                    refresh_token: sessionData.refresh_token,
                  }).catch(() => {});
                }
                await hydrateProfile(sessionData.user);
              }
              if (mounted) setLoading(false);
              return;
            } else {
              sessionStorage.removeItem('supabase-auth-token');
            }
          } catch (parseError) {
            console.error('Error parsing stored session:', parseError);
            sessionStorage.removeItem('supabase-auth-token');
          }
        }

        const authPromise = supabase.auth.getUser();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Auth timeout')), 3000)
        );

        try {
          const { data } = await Promise.race([authPromise, timeoutPromise]);
          if (mounted && data?.user) {
            await hydrateProfile(data.user);
          }
        } catch (authError) {
          // Non-critical — user is simply not logged in or the client timed out
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();

    /*
     * Listen for Supabase auth events (token refresh, external sign-in, etc.).
     * sessionStorage cleanup is intentionally skipped here — only signOut()
     * clears it. Doing it here would wipe the token during signup when
     * setSession() fires a spurious SIGNED_OUT event.
     */
    const { data: listener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          // On token refresh, write the new tokens back to sessionStorage so
          // subsequent hydrateProfile calls don't use an expired access_token.
          if (event === 'TOKEN_REFRESHED' && session.access_token) {
            try {
              const stored = sessionStorage.getItem('supabase-auth-token');
              const existing = stored ? JSON.parse(stored) : {};
              sessionStorage.setItem('supabase-auth-token', JSON.stringify({
                ...existing,
                access_token: session.access_token,
                refresh_token: session.refresh_token || existing.refresh_token,
                expires_at: session.expires_at || existing.expires_at,
                user: session.user,
              }));
            } catch (e) {
              // Non-critical — best effort
            }
          }
          // Always pass the live token from the event to avoid using a stale
          // stored token during the window between expiry and refresh.
          await hydrateProfile(session.user, session.access_token || null);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          setPermissions([]);
        }
      }
    );

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  /* =========================
     INACTIVITY TIMEOUT
  ========================= */

  // 25 min of no activity → show warning; 30 min → auto sign-out.
  useEffect(() => {
    if (!user) {
      clearTimeout(idleWarnRef.current);
      clearTimeout(idleLogoutRef.current);
      setSessionExpiring(false);
      return;
    }

    const resetTimers = () => {
      clearTimeout(idleWarnRef.current);
      clearTimeout(idleLogoutRef.current);
      setSessionExpiring(false);
      idleWarnRef.current = setTimeout(() => setSessionExpiring(true), 25 * 60 * 1000);
      idleLogoutRef.current = setTimeout(() => signOut(), 30 * 60 * 1000);
    };

    const EVENTS = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart'];
    EVENTS.forEach(e => window.addEventListener(e, resetTimers, { passive: true }));
    resetTimers();

    return () => {
      EVENTS.forEach(e => window.removeEventListener(e, resetTimers));
      clearTimeout(idleWarnRef.current);
      clearTimeout(idleLogoutRef.current);
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Resets the idle timer — call when user acknowledges the expiry warning. */
  const extendSession = () => {
    window.dispatchEvent(new MouseEvent('mousemove'));
  };

  /* =========================
     PERMISSION HELPERS
  ========================= */

  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  const hasAnyPermission = (permissionList = []) => {
    return permissionList.some((p) => permissions.includes(p));
  };

  /** Clears all local auth state and signs the user out of Supabase. */
  const signOut = async () => {
    try {
      sessionStorage.removeItem('supabase-auth-token');
      sessionStorage.removeItem('signupData');
      sessionStorage.removeItem('organizationData');

      setUser(null);
      setProfile(null);
      setPermissions([]);

      // Fire-and-forget — local state is already cleared above
      supabase.auth.signOut().catch(err => {
        console.error('Supabase signOut failed (non-critical):', err);
      });
    } catch (error) {
      console.error('Error during sign out:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        permissions,
        loading,
        isAuthenticated: !!user,
        organizationId: profile?.organization_id ?? null,
        isDemo: profile?.organization_id === '00000000-0000-0000-0000-000000000000',
        hasPermission,
        hasAnyPermission,
        signOut,
        sessionExpiring,
        extendSession
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/* =========================
   HOOK
========================= */

export const useAuth = () => useContext(AuthContext);